import React, { useState, useEffect } from 'react';
import { X, Camera, Trash2, Monitor, Link as LinkIcon, Unlink } from 'lucide-react';
import toast from 'react-hot-toast';
import { db } from '../../firebase/config.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { screenManagementService } from '../../services/screenManagementService.js';
import { useAuth } from '../../hooks/useAuth';
import { eventsService } from '../../services/firestoreService';
import { parseFirestoreDate } from '../../utils/dateUtils';
import ConfirmDialog from '../Common/ConfirmDialog';

const EditEventModal = ({ event, isOpen, onClose, onEventUpdated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: '',
    time: '',
    location: '',
    moderation: true,
    allowUploads: true,
    slideSeconds: '8',
    showQr: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmState, setConfirmState] = useState({ open: false, next: null, loading: false, title: '', message: '', variant: 'warning' });
  const [currentStatus, setCurrentStatus] = useState(event?.status || 'draft');
  // Screens linking state
  const [linkedScreens, setLinkedScreens] = useState([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [availableScreens, setAvailableScreens] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);

  const getStatusPill = (status) => {
    const cls = status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>{getStatusText(status)}</span>;
  };

  useEffect(() => {
    if (event && isOpen) {
      try {
        const eventDate = parseFirestoreDate(event.date);
        setFormData({
          name: event.name || '',
          description: event.description || '',
          date: eventDate ? eventDate.toISOString().split('T')[0] : '',
          time: eventDate ? eventDate.toTimeString().slice(0, 5) : '',
          location: event.location || '',
          moderation: typeof event?.photoSettings?.moderation === 'boolean' ? event.photoSettings.moderation : !(event?.settings?.autoApprove === true),
          allowUploads: !(event?.photoSettings?.allowUploads === false),
          slideSeconds: (event?.screenSettings?.slideSeconds || 8).toString(),
          showQr: !(event?.screenSettings?.showQr === false)
        });
        setCurrentStatus(event.status || 'draft');
      } catch (error) {
        console.error('Error parsing event date:', error);
        setFormData({
          name: event.name || '',
          description: event.description || '',
          date: '',
          time: '',
          location: event.location || '',
          moderation: typeof event?.photoSettings?.moderation === 'boolean' ? event.photoSettings.moderation : true,
          allowUploads: !(event?.photoSettings?.allowUploads === false),
          slideSeconds: (event?.screenSettings?.slideSeconds || 8).toString(),
          showQr: !(event?.screenSettings?.showQr === false)
        });
        setCurrentStatus(event.status || 'draft');
        setError('Error al cargar la fecha del evento');
      }
      // Cargar pantallas vinculadas
      (async () => {
        try {
          setScreensLoading(true);
          const id = event.eventId || event.id;
          // Compat: la app usa 'eventId' para enlazar pantallas
          const q = query(collection(db, 'screens'), where('eventId', '==', id));
          const snap = await getDocs(q);
          const list = snap.docs.map(d => ({ screenId: d.id, ...d.data() }));
          setLinkedScreens(list);
        } catch(e) {
          setLinkedScreens([]);
        } finally {
          setScreensLoading(false);
        }
      })();
    }
  }, [event, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // Mantener el esquema: guardar una única fecha (Timestamp) combinando fecha + hora
      const combinedDate = formData.date
        ? new Date(`${formData.date} ${formData.time || '00:00'}`)
        : null;

      const eventData = {
        name: formData.name?.trim() || '',
        description: formData.description?.trim() || '',
        date: combinedDate && !isNaN(combinedDate.getTime()) ? combinedDate : null,
        location: formData.location?.trim() || '',
        'photoSettings.moderation': !!formData.moderation,
        'photoSettings.allowUploads': !!formData.allowUploads,
        'settings.autoApprove': !formData.moderation,
        'screenSettings.slideSeconds': Math.min(10, Math.max(1, parseInt(formData.slideSeconds) || 8)),
        'screenSettings.showQr': !!formData.showQr
      };

      // Usar el identificador correcto del evento
      await eventsService.updateEvent(event.eventId || event.id, eventData);
      onEventUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Error al actualizar el evento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !window.confirm('¿Estás seguro de que quieres eliminar este evento? Esta acción no se puede deshacer.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Usar el identificador correcto del evento
      await eventsService.deleteEvent(event.eventId || event.id);
      onEventUpdated();
      onClose();
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Error al eliminar el evento');
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => (status === 'active' ? 'Activo' : 'Inactivo');

  const changeStatus = async (next) => {
    if (!event) return;
    const id = event.eventId || event.id;
    try {
      await eventsService.updateEvent(id, { status: next });
      // Reflejar el nuevo estado inmediatamente en el modal
      setCurrentStatus(next);
      onEventUpdated && onEventUpdated();
    } catch (e) {
      console.error('Error cambiando estado', e);
      setError('No se pudo cambiar el estado');
    }
  };

  const askChange = (next) => {
    const info = next === 'active'
      ? { t: 'Activar evento', m: 'El evento se activará y será visible públicamente.' }
      : { t: 'Desactivar evento', m: 'El evento quedará inactivo y no será visible públicamente.' };
    setConfirmState({ open: true, next, loading: false, title: info.t, message: info.m, variant: next === 'active' ? 'warning' : 'danger' });
  };

  const confirmStatus = async () => {
    const next = confirmState.next;
    try {
      setConfirmState(s => ({ ...s, loading: true }));
      await changeStatus(next);
      setConfirmState({ open: false, next: null, loading: false });
    } catch (e) {
      setConfirmState(s => ({ ...s, loading: false }));
    }
  };

  const cancelStatus = () => setConfirmState({ open: false, next: null, loading: false });

  // Abrir selector de pantallas disponibles
  const openPicker = async () => {
    try {
      setPickerOpen(true);
      setPickerLoading(true);
      const all = await screenManagementService.getAvailableScreens(user?.uid || '');
      // Disponible = sin eventId asignado (acepta cualquier status)
      const avail = (all || []).filter(s => !s.eventId);
      setAvailableScreens(avail);
    } catch(e) {
      setAvailableScreens([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const linkScreen = async (screen) => {
    try {
      if (linking) return;
      setLinking(true);
      setLinkingId(screen.screenId || screen.id);
      const id = event.eventId || event.id;
      await screenManagementService.linkScreenToEvent(screen.screenId || screen.id, id, user?.uid || 'admin');
      const added = { ...screen, eventId: id };
      setLinkedScreens(prev => [...prev, added]);
      setAvailableScreens(prev => prev.filter(s => (s.screenId || s.id) !== (screen.screenId || screen.id)));
      setPickerOpen(false);
      try { toast.success('Pantalla enlazada'); } catch {}
    } catch(e) {
      // keep modal open, but stop loading
    } finally {
      setLinking(false);
      setLinkingId(null);
    }
  };

  const unlinkScreen = async (screen) => {
    try {
      if (unlinking) return;
      setUnlinking(true);
      setUnlinkingId(screen.screenId || screen.id);
      await screenManagementService.unlinkScreenFromEvent(screen.screenId || screen.id, user?.uid || 'admin');
      setLinkedScreens(prev => prev.filter(s => (s.screenId || s.id) !== (screen.screenId || screen.id)));
      try { toast.success('Pantalla desvinculada'); } catch {}
    } catch(e) {
      // ignore
    } finally {
      setUnlinking(false);
      setUnlinkingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content sm:max-w-lg md:max-w-2xl lg:max-w-4xl w-full flex flex-col h-[90vh]">
        {/* Header fijo */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 flex-none">
          <h2 className="text-xl font-semibold text-gray-900">Editar Evento</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form + Body con scroll y Footer fijo */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          {/* Body scrollable */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna formulario */}
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Evento
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={280}
                  className="input-field"
                />
                <div className="mt-1 text-xs text-gray-500">{(formData.description || '').length}/280</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ubicación
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>

              {/* Campos removidos: Máximo de Participantes y Máximo de Fotos */}

              {/* Configuración de fotos */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Camera size={16} className="mr-2"/> Configuración de fotos</h4>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="moderation"
                    checked={formData.moderation}
                    onChange={(e) => setFormData(prev => ({ ...prev, moderation: e.target.checked }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="moderation" className="ml-2 text-sm text-gray-700">Requerir aprobación antes de mostrar</label>
                </div>
                <div className="flex items-center mt-3">
                  <input
                    type="checkbox"
                    id="allowUploads"
                    checked={!!formData.allowUploads}
                    onChange={(e) => setFormData(prev => ({ ...prev, allowUploads: e.target.checked }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="allowUploads" className="ml-2 text-sm text-gray-700">Permitir que los usuarios suban fotos</label>
                </div>
              </div>

              {/* Pantalla: duración por foto */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Pantalla: duración por foto</h4>
                <label className="block text-sm text-gray-700 mb-2">Segundos por foto</label>
                <select
                  name="slideSeconds"
                  value={formData.slideSeconds}
                  onChange={handleInputChange}
                  className="input-field max-w-[160px]"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(s => (
                    <option key={s} value={s}>{s} {s === 1 ? 'segundo' : 'segundos'}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Afecta la reproducción en las pantallas vinculadas (screen).</p>

                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="showQr"
                    checked={!!formData.showQr}
                    onChange={(e) => setFormData(prev => ({ ...prev, showQr: e.target.checked }))}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showQr" className="ml-2 text-sm text-gray-700">
                    Mostrar QR en la pantalla
                  </label>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}
              </div>

              {/* Columna derecha */}
              <div className="space-y-4">
                {/* Estado y acciones */}
                <div className="card p-4">
              <h3 className="font-medium text-gray-900 mb-3">Estado del Evento</h3>
              <div className="flex items-center gap-2 text-sm text-gray-700 mb-3">
                <span>Actual:</span> {getStatusPill(currentStatus)}
              </div>
              <div className="flex flex-wrap gap-2">
                {currentStatus === 'active' ? (
                  <button type="button" onClick={() => askChange('inactive')} className="btn-danger text-xs">Desactivar</button>
                ) : (
                  <button type="button" onClick={() => askChange('active')} className="btn-primary text-xs">Activar</button>
                )}
              </div>
                </div>

                {/* Pantallas vinculadas */}
                <div className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 flex items-center"><Monitor className="w-4 h-4 mr-2"/> Pantallas vinculadas</h3>
                    <button type="button" onClick={openPicker} className="btn-primary text-xs flex items-center">
                      <LinkIcon className="w-3.5 h-3.5 mr-1"/> Enlazar pantalla
                    </button>
                  </div>
                  {screensLoading ? (
                    <div className="text-sm text-gray-600">Cargando…</div>
                  ) : linkedScreens.length === 0 ? (
                    <div className="text-sm text-gray-600">No hay pantallas vinculadas.</div>
                  ) : (
                    <ul className="divide-y">
                      {linkedScreens.map(sc => (
                        <li key={sc.screenId || sc.id} className="py-2 flex items-center justify-between">
                          <div className="text-sm text-gray-800">
                            {sc.name || sc.code || (sc.screenId || sc.id)}
                          </div>
                          <button type="button" onClick={() => unlinkScreen(sc)} disabled={unlinking} className="btn-secondary text-xs flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
                            <Unlink className="w-3.5 h-3.5 mr-1"/>
                            {(unlinking && (unlinkingId === (sc.screenId || sc.id))) ? 'Quitando…' : 'Quitar'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Zona de Peligro */}
                <div className="card p-4 border-red-200 bg-red-50">
                  <h3 className="font-medium text-red-900 mb-3">Zona de Peligro</h3>
              <p className="text-sm text-red-700 mb-3">
                Las acciones en esta sección son irreversibles.
              </p>
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="btn-danger w-full"
              >
                <Trash2 size={16} className="mr-2" />
                Eliminar Evento
              </button>
            </div>
          </div>
            </div>
          </div>

          {/* Footer fijo */}
          <div className="flex items-center justify-end gap-3 p-4 md:p-5 border-t border-gray-200 flex-none">
            <button type="button" onClick={onClose} disabled={loading} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
      <ConfirmDialog
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmState.loading}
        onConfirm={confirmStatus}
        onCancel={cancelStatus}
        confirmText="Confirmar"
      />

      {/* Modal selector de pantallas */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { if (!linking && !pickerLoading) setPickerOpen(false); }} />
          <div className="relative bg-white rounded-lg shadow-xl w-[min(92vw,720px)] max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h4 className="font-semibold">Seleccionar pantalla</h4>
              <button onClick={() => setPickerOpen(false)} disabled={pickerLoading || linking} className="text-gray-500 hover:text-gray-700 disabled:opacity-50"><X size={18}/></button>
            </div>
            <div className="p-4 overflow-y-auto relative">
              {(pickerLoading || linking) && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
              )}
              {pickerLoading ? (
                <div className="text-sm text-gray-600">Cargando pantallas…</div>
              ) : availableScreens.length === 0 ? (
                <div className="text-sm text-gray-600">No hay pantallas disponibles para enlazar.</div>
              ) : (
                <ul className="divide-y">
                  {availableScreens.map(sc => (
                    <li key={sc.screenId || sc.id} className="py-2 flex items-center justify-between">
                      <div className="text-sm text-gray-800">
                        {sc.name || sc.code || (sc.screenId || sc.id)}
                        {sc.code ? (<span className="ml-2 text-xs text-gray-500">({sc.code})</span>) : null}
                      </div>
                      <button type="button" onClick={() => linkScreen(sc)} disabled={pickerLoading || linking} className="btn-primary text-xs flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
                        <LinkIcon className="w-3.5 h-3.5 mr-1"/>
                        {linking && (linkingId === (sc.screenId || sc.id)) ? 'Enlazando…' : 'Enlazar'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="p-3 border-t flex justify-end">
              <button type="button" onClick={() => setPickerOpen(false)} disabled={pickerLoading || linking} className="btn-secondary disabled:opacity-50">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditEventModal;
